import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiContext from 'src/provider/API/call-service';
import AppInfoContext from 'src/provider/state-manager/appInfoProvider';
import { AppSpan, AppText, CustomContainer, FlexRow, ScreenContainer, ScreenTitle } from 'src/style';
import { DateRangePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import 'react-dates/initialize';
import moment from 'moment';
import {ReactComponent as ArrowUpRight} from 'src/assets/svg/arrow-right-circle.svg'
import {ReactComponent as ArrowDownRight} from 'src/assets/svg/arrow-down-right-circle.svg'
import {ReactComponent as CustomerIconGreen} from 'src/assets/svg/customer-sm-green-icon.svg'
import {ReactComponent as MapIconYellow} from 'src/assets/svg/map-icon-yellow.svg'
import {ReactComponent as MapIconRed} from 'src/assets/svg/map-icon-red.svg'
import {ReactComponent as CompanyIcon} from 'src/assets/svg/company-sm-icon.svg'
import { VerificationChart, Heatmap } from 'src/component';
import { dashboardStats, verificationChart, locationStats, Devices } from 'src/model';
import utility from 'src/utils/utility';


export const CompanyHome: React.FC = () => {
    const navigate = useNavigate()
    const {API} = useContext(ApiContext)
    const {info: {organization}} = useContext(AppInfoContext)
    const [startDate, setStartDate] = useState(moment());
    const [endDate, setEndDate] = useState(moment().add(7, 'd'));
    const [focusedInput, setFocusedInput] = useState(null);
    const [addressStats, setAddressStats] = useState<dashboardStats | null>(null)
    const [verificationChart, setVerificationChart] = useState<verificationChart | null>(null)
    const [deviceStats, setDeviceStats] = useState<any>({})
    const [locationStats, setLocationStats] = useState<Array<locationStats> | null>(null)
    const [verificationRate, setVerificationRate] = useState<any>([])

    useEffect(() => {
        if (organization.id) handleFetchDashboardInfo()
    }, [organization])

    function onDateChange({startDate, endDate}: any) {
        setStartDate(startDate);
        setEndDate(endDate);
        
        if (startDate && endDate && focusedInput === 'endDate') {
            const from = startDate.toISOString()?.split('T')[0]
            const to = endDate.toISOString()?.split('T')[0]

            handleFetchDashboardInfo({from, to})
        }
    }

    function onFocusChange(focusedInput: any) {
        setFocusedInput(focusedInput);
    }

    async function handleFetchDashboardInfo (dateRange = {}, year?: string) {
        const addressStats = await API.getAddressStats(dateRange)
        const verificationChart = await API.getVerificationChart(year)
        const deviceStats = await API.getDeviceStats(dateRange)
        const locationStats = await API.getLocationStats(dateRange)
        const verificationRate = await API.getCompanyVerificationRateStats(dateRange)

        if (addressStats) setAddressStats(addressStats)
        if (verificationChart) setVerificationChart(verificationChart)
        if (deviceStats) {
            const devices: Devices = {macos: 0, android: 0, windows: 0, api: 0, ios: 0}

            for (let item of deviceStats) {
                devices[item.device as keyof Devices] = item.count
            }

            const {macos, android, windows, api, ios} = devices
            let [macosPerc, androidPerc, windowsPerc, iosPerc, apiPerc] = [0, 0, 0, 0, 0]
            const total = macos + android + api + windows + ios
            
            if (total != 0) {
                macosPerc = Number(((macos/total) * 100).toFixed(1))
                androidPerc = Number(((android/total) * 100).toFixed(1))
                windowsPerc = Number(((windows/total) * 100).toFixed(1))
                apiPerc = Number(((api/total) * 100).toFixed(1))
                iosPerc = Number(((ios/total) * 100).toFixed(1))
            }

            const devicesObj = {
                macos: {count: macos, percent: macosPerc},
                android: {count: android, percent: androidPerc},
                windows: {count: windows, percent: windowsPerc},
                api: {count: api, percent: apiPerc},
                ios: {count: ios, percent: iosPerc}
            }

            setDeviceStats(devicesObj)
        }
        if (locationStats) setLocationStats(locationStats)
        if (verificationRate) setVerificationRate(verificationRate)
    }

    const heatmapData =  [
        { date: '2024-01-01', count: 4 },
        { date: '2024-02-02', count: 2 },
        { date: '2024-02-08', count: 1 },
        { date: '2024-03-15', count: 4 },
        { date: '2024-03-16', count: 4 },
    ];

    return (
        <ScreenContainer padding='2.5'>
            <ScreenTitle 
                color='#333333' 
                textSize='2.4' 
                fontWeight='700'
                bottomMargin='4'
            >
                Dashboard
            </ScreenTitle>

            <FlexRow 
                justifyContent='space-between'
                wrap='wrap'
                gap='3'
            >
                <AppSpan textSize='1.8' color='#333333'>Hello Chijindu</AppSpan>
                <DateRangePicker
                    startDate={startDate}
                    startDateId="start_date"
                    endDate={endDate}
                    endDateId="end_date"
                    onDatesChange={onDateChange}
                    focusedInput={focusedInput}
                    onFocusChange={onFocusChange}
                    isOutsideRange={() => false}
                />
            </FlexRow>
            <CustomContainer 
                topMargin='2'
                className='scrollable-container-plus'
            >
                <CustomContainer
                    overflow='auto'
                    style={{display: 'inline-block'}}
                >
                    <FlexRow 
                        gap='1.5'
                        alignItems='stretch' 
                        style={{minWidth: 'fit-content'}}
                    >
                        <CustomContainer
                            width='33.8'
                            minHeight='16.1'
                            radius='0.8'
                            bgColor='#F3F0FF'
                            padding='1.5'
                            style={{borderLeft: '4px solid #5627FF'}}
                        >
                            <FlexRow 
                                justifyContent='space-between'
                                alignItems='flex-start'
                                gap='2.5' bottomMargin='3'
                            >
                                <CustomContainer width='auto' sizeUnit=''>
                                    <AppSpan textSize='#282828'>Subsidiaries</AppSpan>
                                    <AppText textSize='2.4' color='#333333' fontWeight='700'>
                                        {utility.moneyFormat(addressStats?.subsidiaries || 0)}
                                    </AppText>
                                </CustomContainer>
                                <CustomContainer width='auto' sizeUnit='' topMargin='0.5'>
                                    <CompanyIcon className='icon-component' />
                                </CustomContainer>
                            </FlexRow>
                            <AppText 
                                textSize='1.2' color='#333333'
                                className='scroll-x-text'
                            >
                                2 Subsidiaries added recently
                            </AppText>
                        </CustomContainer>

                        <CustomContainer
                            width='33.8'
                            minHeight='16.1'
                            radius='0.8'
                            bgColor='#ECFCF3'
                            padding='1.5'
                            style={{borderLeft: '4px solid #27AE60'}}
                        >
                            <FlexRow 
                                justifyContent='space-between'
                                alignItems='flex-start'
                                gap='2.5'
                            >
                                <CustomContainer width='auto' sizeUnit=''>
                                    <AppSpan textSize='#282828'>All Customers</AppSpan>
                                    <AppText textSize='2.4' color='#333333' fontWeight='700'>
                                        {utility.moneyFormat(addressStats?.customers?.total || 0)}
                                    </AppText>
                                </CustomContainer>
                                <CustomContainer width='auto' sizeUnit='' topMargin='0.5'>
                                    <CustomerIconGreen className='icon-component' />
                                </CustomContainer>
                            </FlexRow>
                            <FlexRow 
                                justifyContent='space-between' 
                                alignItems='flex-start' 
                                topMargin='3'
                                gap='3'
                                className='scroll-x-text'
                            >
                                <AppText textSize='1.2' color='#333333'>
                                    2,000,000 more customer in the last 7 days
                                </AppText>
                                <FlexRow width='auto' sizeUnit='' gap='0.3'>
                                    <AppSpan textSize='1.2' color='#333333' fontWeight='700'>
                                        {addressStats?.customers?.rate}%
                                    </AppSpan>
                                    <ArrowUpRight className='icon-component' />
                                </FlexRow>
                            </FlexRow>
                        </CustomContainer>

                        <CustomContainer
                            width='33.8'
                            minHeight='16.1'
                            radius='0.8'
                            bgColor='#FFFBEE'
                            padding='1.5'
                            style={{borderLeft: '4px solid #F2C94C'}}
                        >
                            <FlexRow 
                                justifyContent='space-between'
                                alignItems='flex-start'
                                gap='2.5'
                            >
                                <CustomContainer width='auto' sizeUnit=''>
                                    <AppSpan textSize='#282828'>Verified Addresses</AppSpan>
                                    <AppText textSize='2.4' color='#333333' fontWeight='700'>
                                        {utility.moneyFormat(addressStats?.verifiedAddresses?.total || 0)}
                                    </AppText>
                                </CustomContainer>
                                <CustomContainer width='auto' sizeUnit='' topMargin='0.5'>
                                    <MapIconYellow className='icon-component' />
                                </CustomContainer>
                            </FlexRow>
                            <FlexRow 
                                justifyContent='space-between' 
                                alignItems='flex-start' 
                                topMargin='3'
                                gap='3'
                                className='scroll-x-text'
                            >
                                <AppText textSize='1.2' color='#333333'>
                                    Verification Rate
                                </AppText>
                                <FlexRow width='auto' sizeUnit='' gap='0.3'>
                                    <AppSpan textSize='1.2' color='#333333' fontWeight='700'>
                                        {addressStats?.verifiedAddresses?.rate}%
                                    </AppSpan>
                                    <ArrowUpRight className='icon-component' />
                                </FlexRow>
                            </FlexRow>
                        </CustomContainer>

                        <CustomContainer
                            width='33.8'
                            minHeight='16.1'
                            radius='0.8'
                            bgColor='#FFEBEB'
                            padding='1.5'
                            style={{borderLeft: '4px solid #EB5757'}}
                        >
                            <FlexRow 
                                justifyContent='space-between'
                                alignItems='flex-start'
                                gap='2.5'
                            >
                                <CustomContainer width='auto' sizeUnit=''>
                                    <AppSpan textSize='#282828'>Unverified Addresses</AppSpan>
                                    <AppText textSize='2.4' color='#333333' fontWeight='700'>
                                        {utility.moneyFormat(addressStats?.unverifiedAddresses?.total || 0)}
                                    </AppText>
                                </CustomContainer>
                                <CustomContainer width='auto' sizeUnit='' topMargin='0.5'>
                                    <MapIconRed className='icon-component' />
                                </CustomContainer>
                            </FlexRow>
                            <FlexRow 
                                justifyContent='space-between' 
                                alignItems='flex-start' 
                                topMargin='3'
                                gap='3'
                                className='scroll-x-text'
                            >
                                <AppText textSize='1.2' color='#333333'>
                                    Unverification Rate
                                </AppText>
                                <FlexRow width='auto' sizeUnit='' gap='0.3'>
                                    <AppSpan textSize='1.2' color='#333333' fontWeight='700'>
                                        {addressStats?.unverifiedAddresses?.rate}%
                                    </AppSpan>
                                    <ArrowDownRight className='icon-component' />
                                </FlexRow>
                            </FlexRow>
                        </CustomContainer>
                    </FlexRow>
                </CustomContainer>
            </CustomContainer>

            <FlexRow
                gap='2'
                topMargin='2'
                justifyContent='space-between'
                className='dashboard-charts-container'
            >
                <CustomContainer
                    width='69' sizeUnit='%' 
                    className='dashboard-chart-wrapper'
                >
                    <AppSpan textSize='1.6' color='#1D1D1D' fontWeight='700'>
                        Address Verification Chart
                    </AppSpan>
                    <CustomContainer 
                        height='47.4'
                        radius='0.8'
                        shadow='0px 10px 50px 0px #0000000D'
                        bgColor='#ffffff'
                        borderColor='#FAFAFA'
                        padding='2'
                        topMargin='1'
                    >
                        <VerificationChart chartData={verificationChart?.monthlyData || []} />
                    </CustomContainer>
                </CustomContainer>
                <CustomContainer
                    width='30' sizeUnit='%'
                    className='dashboard-chart-wrapper'
                >
                    <AppSpan textSize='1.6' color='#1D1D1D' fontWeight='700'>
                        Verification Rate by Companies
                    </AppSpan>
                    <CustomContainer 
                        height='47.4'
                        radius='0.8'
                        shadow='0px 10px 50px 0px #0000000D'
                        bgColor='#ffffff'
                        borderColor='#FAFAFA'
                        padding='2'
                        topMargin='1'
                        overflow='auto'
                    >
                        {
                            verificationRate?.map((item: any, index: number) => {
                                const rate = item.noOfVerifiedAddresses === 0 ? 0 : ((item.noOfVerifiedAddresses/item.noOfUnVerifiedAddresses) * 100)
                                return (
                                    <CustomContainer 
                                        bottomMargin='1.5' bottomPadding='1'
                                        borderBottomColor='#EFEFEF'
                                        key={index}
                                    >
                                        <AppText 
                                            textSize='1.6' color='#1D1D1D' 
                                            fontWeight='700' bottomMargin='1'
                                        >
                                            {item.subsidiary?.name}
                                        </AppText>
                                        <CustomContainer 
                                            bgColor='#EFEFEF' radius='20' bottomMargin='0.5'
                                        >
                                            <CustomContainer 
                                                height='0.8' bgColor='#5627FF' 
                                                radius='20' width={`${rate}`} sizeUnit='%'
                                            />
                                        </CustomContainer>
                                        <AppSpan fontWeight='500'>{rate}% verification rate</AppSpan>
                                    </CustomContainer>
                                )
                            })
                        }
                    </CustomContainer>
                </CustomContainer>
            </FlexRow>

            <FlexRow
                gap='2'
                topMargin='2'
                justifyContent='space-between'
                alignItems='flex-start'
                className='dashboard-charts-container'
            >
                <CustomContainer
                    width='34' sizeUnit='%'
                    className='dashboard-chart-wrapper'
                >
                    <AppSpan textSize='1.6' color='#1D1D1D' fontWeight='700'>
                        Address Verified by Devices
                    </AppSpan>
                    <CustomContainer 
                        minHeight='28.8'
                        radius='0.8'
                        shadow='0px 10px 50px 0px #0000000D'
                        bgColor='#ffffff'
                        borderColor='#FAFAFA'
                        padding='2'
                        topMargin='1'
                    >
                        <CustomContainer bottomMargin='4.5'>
                            <AppText 
                                textSize='3.2' 
                                color='#1D1D1D' 
                                fontWeight='700'
                            >
                                {deviceStats?.windows?.count + deviceStats?.android?.count + deviceStats?.api?.count + deviceStats?.ios?.count}
                            </AppText>
                            <AppSpan textSize='1.2'>Total Devices Pinged</AppSpan>
                        </CustomContainer>
                        <FlexRow>
                            <CustomContainer 
                                height='4.8' width={deviceStats?.android?.percent || '0'} 
                                sizeUnit='%' bgColor='#5627FF' 
                            />
                            <CustomContainer 
                                height='4.8' width={deviceStats?.ios?.percent || '0'} 
                                sizeUnit='%' bgColor='#27AE60' 
                            />
                            <CustomContainer 
                                height='4.8' width={deviceStats?.macos?.percent || '0'} 
                                sizeUnit='%' bgColor='#F2C94C' 
                            />
                            <CustomContainer 
                                height='4.8' width={deviceStats?.windows?.percent || '0'} 
                                sizeUnit='%' bgColor='#F2994A' 
                            />
                            <CustomContainer 
                                height='4.8' width={deviceStats?.api?.percent || '0'} 
                                sizeUnit='%' bgColor='#000000' 
                            />
                        </FlexRow>
                        
                        <FlexRow 
                            justifyContent='space-between' 
                            alignItems='flex-start'
                            gap='2' wrap='wrap'
                            topMargin='3'
                        >
                            {
                                Object.keys(deviceStats)?.map((item, index) => {
                                    return (
                                        <CustomContainer width='auto' sizeUnit='' key={index}>
                                            <AppSpan textSize='1.2'>{utility.toTitleCase(item)}</AppSpan>
                                            <FlexRow 
                                                width='auto' sizeUnit='' topMargin='-0.5' 
                                                gap='0.2' alignItems='flex-end'
                                            >
                                                <AppSpan 
                                                    textSize='1.8' 
                                                    color='#1D1D1D' 
                                                    fontWeight='700'
                                                >
                                                    {deviceStats[item].count}
                                                </AppSpan>
                                                <AppSpan textSize='1.2' bottomMargin='0.2'>{deviceStats[item].percent}%</AppSpan>
                                            </FlexRow>
                                            <CustomContainer height='0.3' width='7' bgColor='#5627FF' />
                                        </CustomContainer>
                                    )
                                })
                            }
                        </FlexRow>
                    </CustomContainer>
                </CustomContainer>

                <CustomContainer
                    width='65' sizeUnit='%' 
                    className='dashboard-chart-wrapper'
                >
                    <AppSpan textSize='1.6' color='#1D1D1D' fontWeight='700'>
                        Ping Time Statistics
                    </AppSpan>
                    <CustomContainer 
                        height='28.8'
                        radius='0.8'
                        shadow='0px 10px 50px 0px #0000000D'
                        bgColor='#ffffff'
                        borderColor='#FAFAFA'
                        padding='2'
                        topMargin='1'
                    >
                        <Heatmap data={heatmapData} />
                    </CustomContainer>
                </CustomContainer>
            </FlexRow>
        </ScreenContainer>
    );
}